import React, { useState } from "react";
import useAuth from "../../hooks/useAuth";
import { useZoomSettings } from "../../hooks/useZoomSettings";
import "./LoginMobile.css";
import { Helmet } from "react-helmet-async";
import { useNavigate, useLocation } from "react-router-dom";
import {
  saveCredentials as saveCredentialsToStorage,
  loadCredentials,
  clearCredentials,
} from "../../utils/encryption";

const LoginMobile = () => {
  const [username, setId] = useState("");
  const [password, setPassword] = useState("");
  const [autoCheck, setKeepLogin] = useState(false);
  const [saveCredentials, setSaveCredentials] = useState(false);
  const { login, loading, error: authError, user } = useAuth();
  const { settings } = useZoomSettings();
  const navigate = useNavigate();
  const location = useLocation();

  // 저장된 자격증명 불러오기
  React.useEffect(() => {
    const credentials = loadCredentials();
    if (credentials) {
      setId(credentials.username);
      setPassword(credentials.password);
      setSaveCredentials(true); // 저장된 자격증명이 있으면 체크박스도 체크
    }
  }, []);

  // 카카오톡 문의하기 클릭 핸들러
  const handleKakaoInquiry = () => {
    if (settings?.kakaoChannelId) {
      window.open(settings.kakaoChannelId, "_blank");
    } else {
      console.warn("카카오톡 채널 ID가 설정되지 않았습니다.");
      alert("카카오톡 문의 준비 중입니다.");
    }
  };

  // Redirect if already logged in
  React.useEffect(() => {
    const getRedirectInfo = () => {
      const params = new URLSearchParams(location.search);
      const redirect = params.get("redirect");
      const tab = params.get("tab");
      return { redirect, tab };
    };

    if (user) {
      const { redirect, tab } = getRedirectInfo();

      if (redirect === "home" && tab) {
        navigate(`/?tab=${tab}`);
      } else {
        navigate("/");
      }
    }
  }, [user, navigate, location.search]);

  const handleLogin = async (e) => {
    e.preventDefault();

    // 로딩 중이면 중복 요청 방지
    if (loading) {
      return;
    }

    // 아이디와 비밀번호 검증
    if (!username || !username.trim()) {
      alert("아이디를 입력해주세요.");
      return;
    }

    if (!password || !password.trim()) {
      alert("비밀번호를 입력해주세요.");
      return;
    }

    // 로그인 전 기존 세션과 토큰 모두 삭제
    // sessionStorage 정리
    try {
      sessionStorage.removeItem("token");
      sessionStorage.removeItem("refresh_token");
      sessionStorage.removeItem("user");
      sessionStorage.removeItem("userInfoLoaded");
      sessionStorage.removeItem("zoomSettings");
      sessionStorage.removeItem("zoomNotification");
    } catch (e) {
      console.warn("세션 정리 중 오류:", e);
    }

    // 메모리 저장소 정리
    if (window._tempStorage) {
      Object.keys(window._tempStorage).forEach((key) => {
        if (
          key.includes("token") ||
          key.includes("user") ||
          key.includes("zoom")
        ) {
          delete window._tempStorage[key];
        }
      });
    }

    const loginData = {
      username,
      password,
      autoCheck,
      auto: true,
    };

    const result = await login(loginData);

    // 로그인 성공 시 자격증명 저장 (체크박스가 선택된 경우에만)
    if (result && result.success && saveCredentials) {
      saveCredentialsToStorage(username, password);
    }
  };

  return (
    <div className="login-mobile-root">
      <Helmet>
        <title>올려줌 - 로그인</title>
        <meta
          name="description"
          content="올려줌에 로그인하여 셀프 마케팅 서비스를 이용하세요."
        />
        <meta name="robots" content="noindex,nofollow" />
        <meta property="og:title" content="올려줌 - 로그인" />
        <meta
          property="og:description"
          content="올려줌에 로그인하여 셀프 마케팅 서비스를 이용하세요."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.olzoom.com/login" />
      </Helmet>

      <div className="login-mobile-container">
        {/* 헤더 영역 */}
        <div className="login-mobile-header">
          <div className="login-mobile-logo">
            <span className="logo-text">올려줌</span>
          </div>
          <div className="login-mobile-subtitle">
            간편하고 쉬운 셀프 마케팅 플랫폼
          </div>
        </div>
        {/* 로그인 폼 */}
        <div className="login-mobile-form-container">
          <form className="login-mobile-form" onSubmit={handleLogin}>
            <div className="login-mobile-input-wrapper">
              <input
                type="text"
                value={username}
                onChange={(e) => setId(e.target.value)}
                placeholder="아이디"
                autoComplete="username"
                className={`login-mobile-input ${
                  username && username.trim() ? "has-value" : ""
                }`}
              />
            </div>

            <div className="login-mobile-input-wrapper">
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="비밀번호"
                autoComplete="current-password"
                className={`login-mobile-input ${
                  password && password.trim() ? "has-value" : ""
                }`}
              />
            </div>

            <div className="login-mobile-checkbox-wrapper">
              <label
                htmlFor="saveCredentials"
                className="login-mobile-checkbox-label"
              >
                <div className="login-mobile-checkbox-container">
                  <input
                    id="saveCredentials"
                    type="checkbox"
                checked={saveCredentials}
                onChange={(e) => setSaveCredentials(e.target.checked)}
                    className="login-mobile-checkbox-input"
                  />
                  <div
                    className={`login-mobile-checkbox-custom ${
                      saveCredentials ? "checked" : ""
                    }`}
                  />
                  {saveCredentials && (
                    <svg
                      className="login-mobile-checkbox-checkmark"
                      viewBox="0 0 9 7"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M1 3.5L3.5 6L8 1"
                        stroke="white"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
                <span className="login-mobile-checkbox-text">
                  아이디와 비밀번호 저장
                </span>
              </label>
            </div>

            <button
              type="submit"
              className="login-mobile-btn"
              disabled={loading}
            >
              {loading ? "로그인 중..." : "로그인"}
            </button>

            {authError && <div className="login-mobile-error">{authError}</div>}
          </form>

          {/* 액션 버튼들 */}
          <div className="login-mobile-actions">
            <button
              type="button"
              className="action-mobile-btn"
              onClick={() => navigate("/signup")}
            >
              회원가입
            </button>
            <button
              type="button"
              className="action-mobile-btn"
              onClick={() => navigate("/find-password")}
            >
              아이디/비밀번호 찾기
            </button>
          </div>

          {/* 도움말 */}
          <div className="login-mobile-help">
            <p>로그인이 되지 않는 경우 카카오톡으로 문의바랍니다.</p>
          </div>

          {/* 카카오톡 문의 버튼 */}
          <button
            type="button"
            className="kakao-mobile-btn"
            onClick={handleKakaoInquiry}
          >
            카카오톡 문의하기
          </button>
        </div>
      </div>
    </div>
  );
};

export default LoginMobile;
